
import * as Sentry from '@sentry/react'
import _ from '@/utils/lodash-optimized'

import http from '../../lib/http'
import * as toastStack from '../shared/toast-stack/actions.js'

export const INIT_STATUS = 'INIT_STATUS'
export const GET_STATUS = 'GET_STATUS'
export const UPDATE_STATUS = 'UPDATE_STATUS'
export const USER_AUTHENTICATED = 'auth/USER_AUTHENTICATED'
export const USER_ALREADY_AUTHENTICATED = 'auth/USER_ALREADY_AUTHENTICATED'
export const USER_SIGNED_OUT = 'auth/USER_SIGNED_OUT'
export const USER_PROFILE_UPDATED = 'auth/USER_PROFILE_UPDATED'
export const USER_PROFILE_APP_METADATA = 'auth/USER_PROFILE_APP_METADATA'
export const USER_AUTHENTICATION_FAILURE = 'auth/USER_AUTHENTICATION_FAILURE'
export const SET_LOGIN_FORM_SCREEN = 'auth/SET_LOGIN_FORM_SCREEN'
export const SET_ERROR = 'auth/SET_ERROR'
export const SET_FLASH_MESSAGE = 'auth/SET_FLASH_MESSAGE'
export const RESET_PASSWORD_FLOW_STARTED = 'auth/RESET_PASSWORD_FLOW_STARTED'
export const NEW_PASSWORD_FLOW_STARTED = 'auth/NEW_PASSWORD_FLOW_STARTED'
export const SIGNIN_FLOW_STARTED = 'auth/SIGNIN_FLOW_STARTED'
export const RESET_STATE = 'auth/RESET_STATE'
export const SET_LOADING = 'auth/SET_LOADING'

export const loginFormInit = () => ({ type: RESET_STATE })

export const userAlreadyAuthenticated = () => ({
  type: USER_ALREADY_AUTHENTICATED,
  redirectTo: '/'
})

export const setLoginFormScreen = (name, meta) => ({
  type: SET_LOGIN_FORM_SCREEN,
  screen: name,
  meta
})

export const setFlashMessage = message => ({
  type: SET_FLASH_MESSAGE,
  message
})

export const setError = error => ({
  type: SET_ERROR,
  error
})

export const userAuthenticationFailure = error => ({
  type: USER_AUTHENTICATION_FAILURE,
  error
})

export const setLoading = value => dispatch =>
  dispatch({
    type: SET_LOADING,
    loading: value
  })

// Legacy heartbeat functionality - now handled by Amplify AuthListener
// Keeping stubs for compatibility with existing code
export const startHeartbeat = () => {
  console.log('🔄 Legacy heartbeat called - now handled by AuthListener')
}

export const stopHeartbeat = () => {
  console.log('🔄 Legacy stopHeartbeat called - now handled by AuthListener')
}

// Password reset - properly implemented with Amplify v6
export const startResetPasswordFlow = (email, dropCurrentUser) => async dispatch => {
  try {
    const { resetPassword } = await import('aws-amplify/auth')

    dispatch({
      type: RESET_PASSWORD_FLOW_STARTED,
      email: email.toLowerCase()
    })

    // Initiate password reset with Amplify v6
    await resetPassword({ username: email.toLowerCase() })

    dispatch(setFlashMessage('Password reset code sent to your email'))
  } catch (error) {
    console.error('Password reset error:', error)
    dispatch(setError(error.message || 'Failed to send password reset code'))
  }
}

const prepareProfile = tkn => {
    console.log('prepareProfile - input token:', tkn)

    // Extract roles from custom attributes or cognito groups
    const roles = tkn['custom:app_metadata_roles'] ||
        tkn['cognito:groups'] ||
        []

    const prepared = _.omitBy(
    Object.assign(tkn, {
        app_metadata: {
            roles: Array.isArray(roles) ? roles : [],
            meta: {
                role: Array.isArray(roles) && roles.length > 0 ? roles[0] : null
            }
        },
      user_metadata: {
        given: tkn.given_name,
        family: tkn.family_name,
        suffix: tkn['custom:name_suffix'] ? [ tkn['custom:name_suffix'] ] : []
      }
    }),
    (value, key) => key.match(/^custom:app_metadata_\d+/)
  )

    console.log('prepareProfile - output profile:', prepared)
    return prepared
}

export const userAuthenticated = profile => dispatch => {
  profile = prepareProfile(profile)

  Sentry.setUser({
    id: profile.sub,
    email: profile.email,
    name: [ profile.given_name, profile.family_name ]
      .map(v => v?.trim())
      .filter(Boolean)
      .join(' ')
  })

  dispatch({
    type: USER_AUTHENTICATED,
    // redirectTo: redirect || "/",
    profile
  })
}

// Legacy Cognito functions - now handled by Amplify
export const startNewPasswordFlow = () => dispatch => {
  dispatch({
    type: NEW_PASSWORD_FLOW_STARTED
  })
}

export const userProfileUpdated = profile => dispatch => {
  profile = prepareProfile(profile)

  Sentry.setUser({
    id: profile.sub,
    email: profile.email,
    name: [ profile.given_name, profile.family_name ]
      .map(v => v?.trim())
      .filter(Boolean)
      .join(' ')
  })

  dispatch({
    type: USER_PROFILE_UPDATED,
    profile
  })
}

// Status functions - these are used by other parts of the app

const STATUS = {
  timeout: 1,
  get: {
    current: 0,
    completed: 0
  },
  set: {
    current: 0,
    completed: 0
  }
}

export function getStatus () {
  return dispatch => {
    if (
      STATUS.get.current === STATUS.get.completed &&
      STATUS.set.current === STATUS.set.completed
    ) {
      const progress = ++STATUS.get.current
      dispatch({ type: INIT_STATUS })
      http.getRequest('/status')
        .then(data => {
          if (progress > STATUS.get.completed) {
            STATUS.get.completed = progress
          }
          if (progress === STATUS.get.current) {
            dispatch({
              type: GET_STATUS,
              data
            })
          }
        })
        .catch(err => {
          console.error(err)
        })
    }
  }
}

const putStatus = _.debounce(
  (params, resolve, reject) =>
    http
      .putRequest(
        '/status',
        _.pick(params, [ 'onduty', 'monitoring', 'oncall', 'concierge' ])
      )
      .then(resolve)
      .catch(reject),
  STATUS.timeout
)

export function setStatus (params) {
  return dispatch => {
    const progress = ++STATUS.set.current
    dispatch({
      type: UPDATE_STATUS,
      data: params
    })

    new Promise((resolve, reject) => putStatus(params, resolve, reject))
      .then(
        data => {
          if (progress > STATUS.set.completed) {
            STATUS.set.completed = progress
          }
          if (progress === STATUS.set.current) {
            dispatch({
              type: GET_STATUS,
              data
            })
            if (params.concierge) {
              // dispatch(getChats());
            }
          }
        }
      )
  }
}

// Legacy logout - now handled by Amplify but keeping for compatibility
export const logout = (discardDialog, callback) => dispatch => {
  console.log('🔄 Legacy logout called - now handled by Amplify AuthListener')
  // Just dispatch Redux cleanup
  dispatch({
    type: USER_SIGNED_OUT,
    forceRedirect: true
  })
  dispatch(toastStack.reset())
  callback?.()
}

// Sign in - properly implemented with Amplify v6
export const signin = (email, password) => async dispatch => {
  dispatch({
    type: SIGNIN_FLOW_STARTED,
    email: email.toLowerCase().trim()
  })

  try {
    const { signIn } = await import('aws-amplify/auth')
    const result = await signIn({
      username: email.toLowerCase().trim(),
      password
    })

    // Handle different sign-in results
    if (result.isSignedIn) {
      // User is successfully signed in
      console.log('✅ Sign in successful')
    } else if (result.nextStep) {
      // Additional steps required (MFA, new password, etc.)
      console.log('Additional step required:', result.nextStep)
    }

    return result
  } catch (error) {
    console.error('Sign in error:', error)
    dispatch(userAuthenticationFailure(error))
    throw error
  }
}

// Session sign in - check if user has existing session
export const signinFromCognitoSession = async dispatch => {
  try {
    const { getCurrentUser, fetchAuthSession } = await import('aws-amplify/auth')

    // Check if user has current session
    const user = await getCurrentUser()
    const session = await fetchAuthSession()

    if (user && session.tokens) {
      console.log('✅ Existing session found')
      return { user, session }
    }
  } catch (error) {
    console.debug('No existing session found')
  }

  return null
}
